/**
 * ThresholdScene — immersive loading transition (AGE-1026, AGE-1316, AGE-1327).
 *
 * A cinematic threshold screen that masks the 3–5s overworld load behind
 * atmosphere: a single painted Ghibli-style background with a gentle tint
 * shift and poetic flavor text.
 *
 * Flow: TitleScene/CharacterCreation → ThresholdScene → OverworldScene
 * ThresholdScene launches OverworldScene in the background, holds for a
 * minimum of 10s, then crossfades out once overworld signals readiness via
 * the `overworld:ready` registry key.
 */

import * as Phaser from 'phaser';
import {
  THRESHOLD_SCENE_KEY,
  PAINTED_WORLD_COORDINATOR_SCENE_KEY,
  TITLE_SCENE_KEY,
} from './sceneKeys';

// ── Palette ─────────────────────────────────────────────────────────────────
const COLOR_DUSK_SAND = '#C4AD8F';

// ── Timing ──────────────────────────────────────────────────────────────────
const MIN_HOLD_MS = 3000;
const FLAVOR_FADE_OUT_MS = 400;
const PAUSE_AFTER_FADE_MS = 150;
const CROSSFADE_MS = 800;
const TIMEOUT_HINT_MS = 8000;
const TIMEOUT_BAIL_MS = 15000;

// ── Tint shift (subtle time-of-day progression over 15s) ────────────────────
const TINT_PHASES: Array<{ timeSec: number; tint: number }> = [
  { timeSec: 0, tint: 0xffd8c0 },   // warm peach
  { timeSec: 7, tint: 0xffffff },   // neutral (no tint)
  { timeSec: 15, tint: 0xfff5e0 },  // warm cream
];

const TITLE_FONT = "'Almendra', serif";

const FLAVOR_LINES = [
  'The hearth remembers your warmth.',
  'Somewhere, a lantern is lit for you.',
  'The wind carries old songs tonight.',
  'A door left open, a path well-worn.',
  'The world stirs — it knows you are near.',
  'Embers glow beneath the quiet.',
  'Every journey begins with a single breath.',
  'The trees lean in, listening.',
];

const REASSURING_LINES = [
  'Almost there.',
  'The world is ready.',
  'A place has been kept for you.',
  'The hearth is warm.',
];

export class ThresholdScene extends Phaser.Scene {
  public static readonly KEY = THRESHOLD_SCENE_KEY;

  private flavorText: Phaser.GameObjects.Text | null = null;
  private reassuringText: Phaser.GameObjects.Text | null = null;
  private hintText: Phaser.GameObjects.Text | null = null;
  private returnButton: Phaser.GameObjects.Text | null = null;

  private bgImage: Phaser.GameObjects.Image | null = null;

  private overworldReady = false;
  private minHoldElapsed = false;
  private exitStarted = false;

  /** Data passed through from TitleScene / CharacterCreation. */
  private passData: Record<string, unknown> = {};

  constructor() {
    super({ key: THRESHOLD_SCENE_KEY });
  }

  init(data: Record<string, unknown>): void {
    this.passData = data ?? {};
    this.overworldReady = false;
    this.minHoldElapsed = false;
    this.exitStarted = false;
    this.flavorText = null;
    this.reassuringText = null;
    this.hintText = null;
    this.returnButton = null;
    this.bgImage = null;
  }

  create(): void {
    const w = this.scale.width;
    const h = this.scale.height;

    // ── Single painted background ────────────────────────────────────────
    if (this.textures.exists('threshold-dawn')) {
      this.bgImage = this.add.image(w / 2, h / 2, 'threshold-dawn');
      const scaleX = w / this.bgImage.width;
      const scaleY = h / this.bgImage.height;
      this.bgImage.setScale(Math.max(scaleX, scaleY));
      this.bgImage.setOrigin(0.5, 0.5);
      this.bgImage.setDepth(0);
      this.bgImage.setTint(TINT_PHASES[0].tint);
    }

    // ── Subtle tint shift over 15s ───────────────────────────────────────
    this.scheduleTintShift();

    // ── Flavor text — first line at second 4 ────────────────────────────
    const line = Phaser.Math.RND.pick(FLAVOR_LINES);
    this.flavorText = this.add
      .text(w / 2, h / 2, line, {
        fontFamily: TITLE_FONT,
        fontSize: '18px',
        color: COLOR_DUSK_SAND,
        align: 'center',
        wordWrap: { width: w * 0.8 },
      })
      .setOrigin(0.5, 0.5)
      .setAlpha(0)
      .setDepth(10);

    this.time.delayedCall(800, () => {
      if (this.flavorText) {
        this.tweens.add({
          targets: this.flavorText,
          alpha: 1,
          duration: 1200,
          ease: 'Sine.easeIn',
        });
      }
    });

    // ── Second reassuring line at second 9 ──────────────────────────────
    const reassuringLine = Phaser.Math.RND.pick(REASSURING_LINES);
    this.reassuringText = this.add
      .text(w / 2, h / 2 + 30, reassuringLine, {
        fontFamily: TITLE_FONT,
        fontSize: '14px',
        color: COLOR_DUSK_SAND,
        align: 'center',
        fontStyle: 'italic',
      })
      .setOrigin(0.5, 0.5)
      .setAlpha(0)
      .setDepth(10);

    this.time.delayedCall(2200, () => {
      if (this.reassuringText) {
        this.tweens.add({
          targets: this.reassuringText,
          alpha: 0.8,
          duration: 1200,
          ease: 'Sine.easeIn',
        });
      }
    });

    // ── Launch overworld in background ──────────────────────────────────
    this.scene.launch(PAINTED_WORLD_COORDINATOR_SCENE_KEY, this.passData);
    this.scene.bringToTop(THRESHOLD_SCENE_KEY);

    // Listen for overworld ready
    this.registry.events.on('changedata-overworld:ready', this.onOverworldReady, this);

    // Check if already set (race condition safety)
    if (this.registry.get('overworld:ready') === true) {
      this.overworldReady = true;
    }

    // Min hold timer
    this.time.delayedCall(MIN_HOLD_MS, () => {
      this.minHoldElapsed = true;
      this.tryExit();
    });

    // 12s timeout hint ("still waking...")
    this.time.delayedCall(TIMEOUT_HINT_MS, () => {
      if (this.exitStarted) return;
      this.hintText = this.add
        .text(w / 2, h / 2 + 60, 'still waking...', {
          fontFamily: TITLE_FONT,
          fontSize: '12px',
          color: COLOR_DUSK_SAND,
          align: 'center',
        })
        .setOrigin(0.5, 0.5)
        .setAlpha(0.5)
        .setDepth(10);
    });

    // 20s timeout bail button
    this.time.delayedCall(TIMEOUT_BAIL_MS, () => {
      if (this.exitStarted) return;
      this.returnButton = this.add
        .text(w / 2, h / 2 + 90, 'Return', {
          fontFamily: TITLE_FONT,
          fontSize: '16px',
          color: COLOR_DUSK_SAND,
          align: 'center',
        })
        .setOrigin(0.5, 0.5)
        .setAlpha(1)
        .setDepth(10)
        .setInteractive({ useHandCursor: true });

      this.returnButton.on('pointerover', () => this.returnButton?.setColor('#ffffff'));
      this.returnButton.on('pointerout', () => this.returnButton?.setColor(COLOR_DUSK_SAND));
      this.returnButton.on('pointerdown', () => {
        this.cleanupAndReturn();
      });
    });
  }

  // ── Tint shift ────────────────────────────────────────────────────────

  private scheduleTintShift(): void {
    if (!this.bgImage) return;
    for (let i = 1; i < TINT_PHASES.length; i++) {
      const phase = TINT_PHASES[i];
      const delayMs = phase.timeSec * 1000;
      this.time.delayedCall(delayMs, () => {
        if (this.bgImage) {
          this.bgImage.setTint(phase.tint);
        }
      });
    }
  }

  // ── Exit logic ────────────────────────────────────────────────────────

  private onOverworldReady = (_parent: unknown, value: unknown): void => {
    if (value === true) {
      this.overworldReady = true;
      this.tryExit();
    }
  };

  private tryExit(): void {
    if (this.exitStarted) return;
    if (!this.overworldReady || !this.minHoldElapsed) return;
    this.exitStarted = true;
    this.beginCrossfade();
  }

  private beginCrossfade(): void {
    const targets: Phaser.GameObjects.GameObject[] = [];
    if (this.flavorText) targets.push(this.flavorText);
    if (this.reassuringText) targets.push(this.reassuringText);
    if (this.hintText) targets.push(this.hintText);
    if (this.returnButton) targets.push(this.returnButton);

    if (targets.length > 0) {
      this.tweens.add({
        targets,
        alpha: 0,
        duration: FLAVOR_FADE_OUT_MS,
        ease: 'Sine.easeOut',
        onComplete: () => this.doCrossfade(),
      });
    } else {
      this.doCrossfade();
    }
  }

  private doCrossfade(): void {
    this.time.delayedCall(PAUSE_AFTER_FADE_MS, () => {
      // Fade out to warm cream (matches settling painting)
      this.cameras.main.fadeOut(CROSSFADE_MS, 0xf5, 0xe6, 0xd0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.cleanup();
        this.scene.stop(THRESHOLD_SCENE_KEY);
      });
    });
  }

  private cleanupAndReturn(): void {
    this.cleanup();
    if (this.scene.isActive(PAINTED_WORLD_COORDINATOR_SCENE_KEY)) {
      this.scene.stop(PAINTED_WORLD_COORDINATOR_SCENE_KEY);
    }
    this.registry.remove('overworld:ready');
    this.scene.start(TITLE_SCENE_KEY);
  }

  private cleanup(): void {
    this.registry.events.off('changedata-overworld:ready', this.onOverworldReady, this);
  }
}
